/**
 * Defensive parsing for Savetnik chat session messages.
 *
 * `mktg_mirror_savetnik_sessions.messages` is jsonb that crossed the FDW
 * mirror boundary from another database (www's os_chat_sessions.messages —
 * see api/os/chat.ts for the shape this repo's own chat sessions use:
 * `{ role, content, timestamp }`). It is untyped at the wire and untrusted
 * at the value level; anything that is not a recognizable {role, text} pair
 * renders as a raw fallback block instead of crashing the drawer.
 */

export interface TranscriptMessageEntry {
  kind: 'message';
  role: string;
  text: string;
}

export interface TranscriptUnknownEntry {
  kind: 'unknown';
  raw: unknown;
}

export type TranscriptEntry = TranscriptMessageEntry | TranscriptUnknownEntry;

const ROLE_KEYS = ['role', 'direction', 'sender'] as const;
const TEXT_KEYS = ['content', 'text', 'message'] as const;

function firstNonEmptyString(
  candidate: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = candidate[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

/** Parses a jsonb `messages` value into a defensive, render-ready list. Never throws. */
export function parseTranscriptMessages(value: unknown): TranscriptEntry[] {
  if (!Array.isArray(value)) return [];

  return value.map((entry): TranscriptEntry => {
    if (typeof entry !== 'object' || entry === null) {
      return { kind: 'unknown', raw: entry };
    }

    const candidate = entry as Record<string, unknown>;
    const role = firstNonEmptyString(candidate, ROLE_KEYS);
    const text = firstNonEmptyString(candidate, TEXT_KEYS);

    if (role !== null && text !== null) {
      return { kind: 'message', role, text };
    }
    return { kind: 'unknown', raw: entry };
  });
}

/** The Savetnik/assistant side of the conversation, for left/right bubble alignment. */
export function isOperatorRole(role: string): boolean {
  return role === 'assistant' || role === 'bot' || role === 'savetnik';
}
